import { useMemo } from "react"
import {
  useAnalytics,
  useAnalyticsWithProperties,
  EMPTY_ANALYTICS_PROPERTIES,
  type Analytics,
  type TrackingProperties,
} from "connected-components/analytics/context"
import { useKellySurfaceProperties } from "../analytics/surface-context"

// "AI Teammate" keeps the existing Mixpanel reports whole; "Kelly" tells the new full-page chat apart from
// the old drawer while both exist (APP-3438). Renaming to Kelly alone waits for the drawer's retirement.
export const KELLY_CONTEXT_PREFIXES = ["AI Teammate", "Kelly"] as const

/** The prefixes to add, or `undefined` when a context above has already added them. */
export function useKellyPrefixContribution(): typeof KELLY_CONTEXT_PREFIXES | undefined {
  const parentPrefixes = useAnalytics().contextProperties.contextPrefixes ?? []
  const isAnyPrefixAlreadySet = KELLY_CONTEXT_PREFIXES.some((prefix) => parentPrefixes.includes(prefix))
  return isAnyPrefixAlreadySet ? undefined : KELLY_CONTEXT_PREFIXES
}

/**
 * For the three call sites ABOVE the dock, which no `AnalyticsContext` mounted inside it can reach. Everything
 * in the dock reads `useAnalytics()` instead.
 *
 * Surface properties merge UNDER the caller's, so a call site can still name an event-scoped `threadId`.
 */
export function useKellyAnalytics(contextProperties: TrackingProperties = EMPTY_ANALYTICS_PROPERTIES): Analytics {
  const surfaceProperties = useKellySurfaceProperties()
  const contextPrefix = useKellyPrefixContribution()

  const mergedProperties = useMemo(
    () => ({ ...surfaceProperties, ...contextProperties }),
    [surfaceProperties, contextProperties],
  )

  return useAnalyticsWithProperties(mergedProperties, contextPrefix)
}
